import parse from 'date-fns/parse';
import subDays from 'date-fns/subDays';
import { formatDate } from 'helpers/date';
import * as Rx from 'rxjs';
import { debounceTime, finalize, map, switchMap, tap } from 'rxjs/operators';
import { MovementsType, SearchType, TransactionsType } from 'v2/interfaces/financial';
import type {
  FinancialStatementEntry,
  FinancialStatementEntryDetails,
  FinancialStatementFilter,
  BodyExportRequest
} from 'v2/interfaces/financial';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import apiService from 'v2/services/api';
import type { ApiService } from 'v2/services/api';

export type ListOrder = 'ASC' | 'DESC';

export const InitialPagination: IPaginationParams = {
  current_page: 1,
  per_page: 15
};

export const initialFilter: FinancialStatementFilter = {
  periodPreset: '7',
  dates: [formatDate(subDays(new Date(), 7), 'dd/MM/yyyy'), formatDate(new Date(), 'dd/MM/yyyy')],
  movement_type: MovementsType.ALL,
  transaction_type: TransactionsType.ALL,
  search_type: SearchType.ALL,
  search_text: ''
};

class FinancialStatement {
  private filters$ = new Rx.BehaviorSubject<FinancialStatementFilter>(initialFilter);
  private loading$ = new Rx.BehaviorSubject<boolean>(true);
  private currentBalance$ = new Rx.BehaviorSubject<number>(null);

  private loadingDetails$ = new Rx.BehaviorSubject<boolean>(true);
  private financialStatement$ = new Rx.BehaviorSubject<FinancialStatementEntry[]>(null);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>(InitialPagination);
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  private financialStatementDetails$ = new Rx.BehaviorSubject<FinancialStatementEntryDetails[]>(null);
  private paginationParamsDetails$ = new Rx.BehaviorSubject<IPaginationParams>(InitialPagination);
  private paginationResponseDetails$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  constructor(private apiServices: ApiService) {}

  public setFilter(filter: FinancialStatementFilter): void {
    this.loading$.next(true);
    this.filters$.next({ ...this.filters$.value, ...filter });
  }

  public getFilters = (): Rx.Observable<FinancialStatementFilter> => this.filters$;
  public getCurrentBalance = (): Rx.Observable<number> => this.currentBalance$;

  public getFinancialStatement = (): Rx.Observable<FinancialStatementEntry[]> => this.financialStatement$;
  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;
  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public getFinancialStatementDetails = (): Rx.Observable<FinancialStatementEntryDetails[]> =>
    this.financialStatementDetails$;
  public getPaginationParamsDetails = (): Rx.Observable<IPaginationParams> => this.paginationParamsDetails$;
  public getPaginationResponseDetails = (): Rx.Observable<IPaginationResponse> => this.paginationResponseDetails$;

  public isLoading = () => this.loading$;
  public isLoadingDetails = () => this.loadingDetails$;

  public setCurrentBalance = (currBalance: number): void => this.currentBalance$.next(currBalance);
  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public resetPage = () => {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });
    return this.getPaginationParams();
  };

  public getNextPageDetails(pagination: IPaginationParams): void {
    this.paginationParamsDetails$.next(pagination);
  }

  public resetPageDetails = () => {
    this.financialStatementDetails$.next(undefined);
    this.paginationParamsDetails$.next({ ...this.paginationParamsDetails$.value, current_page: 1 });
    return this.getPaginationParamsDetails();
  };

  public getTotalAvailable = () => this.apiServices.get('financial-statement/balance/available-total');
  public getAvailable = () => this.apiServices.get('financial-statement/balance/available');
  public getAvailableInFuture = () => this.apiServices.get('financial-statement/balance/available-in-future');
  public getAnticipationPromotion = () => this.apiServices.get('anticipation/promotions');
  public getAnticipationData = () => this.apiServices.get('anticipation/credit-days');
  public setAnticipationPeriod = (period: number, fromSuggestion = false) =>
    this.apiServices.put('anticipation/credit-days', { days: period, fromSuggestion });

  public formatFilter = (filter: FinancialStatementFilter) => {
    const newFilter: Partial<FinancialStatementFilter> = {};

    newFilter.transacted_at_initial = formatDate(parse(filter.dates[0], 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');
    newFilter.transacted_at_final = formatDate(parse(filter.dates[1], 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');

    if (filter.transaction_type) {
      newFilter.transaction_type = filter.transaction_type;
    }

    if (filter.movement_type) {
      newFilter.movement_type = filter.movement_type;
    }

    if (filter.search_text) {
      newFilter.search_type = filter.search_type;
      newFilter.search_text = filter.search_text;
    }

    return newFilter;
  };

  public requestExport = (body: BodyExportRequest) =>
    this.apiServices.post('financial-statement/export-in-background', { ...body });

  public formatDetailsFilter = (filter: FinancialStatementFilter) => {
    const newDetailsFilter: Partial<FinancialStatementFilter> = {};

    if (filter.transaction_type) {
      newDetailsFilter.transaction_type = filter.transaction_type;
    }

    if (filter.movement_type) {
      newDetailsFilter.movement_type = filter.movement_type;
    }

    if (filter.search_text) {
      newDetailsFilter.search_type = filter.search_type;
      newDetailsFilter.search_text = filter.search_text;
    }

    return newDetailsFilter;
  };

  public loadData() {
    return this.getFilters().pipe(
      map(filter => this.formatFilter(filter)),
      switchMap(filter => {
        return this.resetPage().pipe(
          map(params => ({
            ...filter,
            ...{
              current_page: params.current_page < 0 ? 1 : params.current_page,
              per_page: params.per_page
            }
          }))
        );
      }),
      debounceTime(10),
      switchMap(filter => {
        this.loading$.next(true);
        return this.apiServices.get('financial-statement', filter).pipe(finalize(() => this.loading$.next(false)));
      }),
      tap(response => this.financialStatement$.next(response.data)),
      tap(response => this.paginationResponse$.next(response.pagination))
    );
  }

  public loadDetails(transactionDate: string) {
    return this.getFilters().pipe(
      map(filter => this.formatDetailsFilter(filter)),
      switchMap(filter =>
        this.resetPageDetails().pipe(
          map(params => ({
            ...filter,
            transaction_date: transactionDate,
            current_page: params.current_page,
            per_page: params.per_page
          }))
        )
      ),
      debounceTime(10),
      switchMap(filter => {
        this.loadingDetails$.next(true);
        return this.apiServices
          .get('financial-statement/detailed', filter)
          .pipe(finalize(() => this.loadingDetails$.next(false)));
      }),
      tap(response => this.financialStatementDetails$.next(response.data)),
      tap(response => this.paginationResponseDetails$.next(response.pagination))
    );
  }
}

export const financialStatementService = new FinancialStatement(apiService);
